import {
	type AutocompleteProps as _AutocompleteProps,
	autocompleteClasses,
	type ChipTypeMap,
	chipClasses,
	inputBaseClasses,
	Autocomplete as MuiAutocomplete,
	outlinedInputClasses,
	styled,
	TextField,
} from "@mui/material"
import type { ComponentProps, ElementType } from "react"

type AutocompleteStyledProps = {
	noBorder?: boolean
}

export const StyledAutocomplete = styled(MuiAutocomplete, {
	shouldForwardProp: (prop) => !(prop === "noBorder" || prop === "borderless"),
})<AutocompleteStyledProps>(({ theme }) => ({
	[`& .${outlinedInputClasses.root}`]: {
		padding: theme.spacing(0.5),
	},
	[`& .${outlinedInputClasses.root} .${autocompleteClasses.input}`]: {
		paddingLeft: theme.spacing(0.5),
		fontSize: theme.typography.fontSize,
	},
	[`& .${outlinedInputClasses.root} .${chipClasses.root}`]: {
		marginTop: 0,
		marginBottom: 0,
	},
	variants: [
		{
			props: { noBorder: true },
			style: {
				"& fieldset": {
					border: "none",
				},
			},
		},
	],
}))
export const StyledAutocompletePaper = styled("div", {
	shouldForwardProp: (prop) => !(prop === "noBorder" || prop === "borderless"),
})(() => ({
	marginTop: 10,
	background: "white",
	borderRadius: "4px",
	minWidth: "400px",
	boxShadow: "0px 0px 10px 2px rgba(0,0,0,0.3)",
}))

export const StyledAutocompleteTextField = styled(TextField, {
	shouldForwardProp: (prop) => !(prop === "noBorder" || prop === "borderless"),
})(({ theme }) => ({
	[`& .${inputBaseClasses.root}`]: {
		padding: theme.spacing(0.5),
	},
}))

// some props from mui are not typed correctly
// ex: multiple is typed as true | false, but it does not comply with the real boolean type
// only with a literal, messing with React props
export type AutocompleteProps<
	Value,
	Multiple extends boolean | undefined = false,
	DisableClearable extends boolean | undefined = false,
	FreeSolo extends boolean = false,
	ChipComponent extends ElementType = ChipTypeMap["defaultComponent"],
> = Omit<
	_AutocompleteProps<Value, Multiple, DisableClearable, FreeSolo, ChipComponent>,
	"freeSolo" | "renderInput" | "multiple" | "disableClearable"
> &
	AutocompleteStyledProps & {
		freeSolo?: boolean
		renderInputLabel?: string
		multiple?: boolean
		renderInput?: _AutocompleteProps<Value, Multiple, DisableClearable, FreeSolo, ChipComponent>["renderInput"]
		disableClearable?: boolean
	}

export const Autocomplete = <
	Value,
	Multiple extends boolean | undefined = false,
	DisableClearable extends boolean | undefined = false,
	FreeSolo extends boolean = false,
	ChipComponent extends ElementType = ChipTypeMap["defaultComponent"],
>({
	renderInputLabel,
	...props
}: AutocompleteProps<Value, Multiple, DisableClearable, FreeSolo, ChipComponent>) => (
	<StyledAutocomplete
		clearOnBlur={false}
		clearOnEscape={false}
		handleHomeEndKeys
		{...(props as ComponentProps<typeof StyledAutocomplete>)}
		{...(renderInputLabel
			? { renderInput: (params) => <StyledAutocompleteTextField {...params} label={renderInputLabel} /> }
			: {})}
		slots={{ paper: (params) => <StyledAutocompletePaper {...params} /> }}
	/>
)
